// Import Bugsnag and the Angular integration
import Bugsnag from '@bugsnag/js'
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular'
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core'
import { environment } from "../environments/environment";
import { REDACTED_KEYS, sanitiseBreadcrumb, sanitiseEventRequest } from "./services/log-sanitiser";

var envStage = environment.production ? 'production' : environment.staging ? 'staging' : 'development';

// configure Bugsnag asap
Bugsnag.start( {
	apiKey: environment.bugsnagId,
	enabledReleaseStages: ['production', 'staging'],
	releaseStage: envStage,

	// DEV-7386: the notifier leaves a breadcrumb of its own for every XHR, fetch and
	// navigation, and records the full URL there - so a per-lead capability token
	// reaches Bugsnag on a *successful* request without any logging of ours being
	// involved. Sanitising our own console calls cannot cover that; scrub every
	// breadcrumb on the way out instead, which also covers call sites added later.
	redactedKeys: REDACTED_KEYS,
	onBreadcrumb: sanitiseBreadcrumb,

	// onBreadcrumb cannot reach event.request.url - see sanitiseEventRequest().
	onError: sanitiseEventRequest,
} )

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory() {
	return new BugsnagErrorHandler()
}

import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ApiService } from './services/api.service';
import { TrackingService } from './services/tracking.service';
import { AffiliateConfigService } from './services/affiliate-config.service';
import { FormSessionInterceptor } from './services/form-session.interceptor';

// Material
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ErrorStateMatcher, MatNativeDateModule, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

// Template Components

// Form parts
import { HeaderComponent } from './parts/header/header.component';
import { FooterComponent } from './parts/footer/footer.component';
import { ProgressBarComponent } from './parts/progress-bar/progress-bar.component';

// Form steps
import { MovingFromComponent } from './form-steps/moving-from/moving-from.component';
import { MovingToComponent } from './form-steps/moving-to/moving-to.component';
import { MovingSizeComponent } from './form-steps/moving-size/moving-size.component';
import { MovingContentComponent } from './form-steps/moving-content/moving-content.component';
import { MovingBoxesComponent } from './form-steps/moving-boxes/moving-boxes.component';
import { UploadPicturesComponent } from './form-steps/upload-pictures/upload-pictures.component';
import { PropertyTypeFromComponent } from './form-steps/property-type-from/property-type-from.component';
import { PropertyTypeToComponent } from './form-steps/property-type-to/property-type-to.component';
import {
	MovingDateComponent,
	customDatePickerHeader
} from './form-steps/moving-date/moving-date.component';
import { NameEmailPhoneComponent } from './form-steps/name-email-phone/name-email-phone.component';
import { QuestionComponent } from './post-submit/third-party-questions/question/question.component';
import { SubQuestionComponent } from './post-submit/third-party-questions/question/sub-question/sub-question.component';

import { VolumeCalculatorComponent } from './form-parts/volume-calculator/volume-calculator.component';
import { APP_BASE_HREF, DatePipe, NgOptimizedImage } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { BackgroundComponent } from './parts/background/background.component';
import { PostSubmitComponent } from './post-submit/post-submit.component';
import { PostSubmitThirdPartyQuestionsComponent } from './post-submit/third-party-questions/third-party-questions.component';
import { PostSubmitVerificationComponent } from './post-submit/verification/verification.component';
import { ThankYouStepComponent } from './post-submit/thank-you/thank-you.component';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { PossibleMatchesComponent } from './form-parts/possible-matches/possible-matches.component';
import { A11yModule } from "@angular/cdk/a11y";
import { PropertyTypeDetailsFromComponent } from './form-steps/property-type-details-from/property-type-details-from.component';
import { PropertyTypeDetailsToComponent } from './form-steps/property-type-details-to/property-type-details-to.component';
import { ConsentMoversComponent } from './form-steps/consent-movers/consent-movers.component';
import { ConsentMoversModalComponent } from './form-parts/consent-movers-modal/consent-movers-modal.component';
import { ConsentMoversListComponent } from './shared-components/consent-movers-list/consent-movers-list.component';
import { ChangePhoneModalComponent } from './form-parts/change-phone-modal/change-phone-modal.component';


@NgModule( {
    declarations: [
        AppComponent,
        // Form parts
        HeaderComponent,
        FooterComponent,
        ProgressBarComponent,
        // Form steps
        MovingFromComponent,
        MovingToComponent,
        MovingSizeComponent,
        MovingContentComponent,
        MovingDateComponent,
        NameEmailPhoneComponent,
        VolumeCalculatorComponent,
        customDatePickerHeader,
        BackgroundComponent,
        PostSubmitComponent,
        PostSubmitThirdPartyQuestionsComponent,
        PostSubmitVerificationComponent,
        ThankYouStepComponent,
        SubQuestionComponent,
        QuestionComponent,
        PossibleMatchesComponent,
        PropertyTypeFromComponent,
        PropertyTypeToComponent,
        PropertyTypeDetailsFromComponent,
        PropertyTypeDetailsToComponent,
        MovingBoxesComponent,
        UploadPicturesComponent,
        ConsentMoversComponent,
        ConsentMoversModalComponent,
        ConsentMoversListComponent,
        ChangePhoneModalComponent,
    ],
	imports: [
		BrowserModule,
		ReactiveFormsModule,
		BrowserAnimationsModule,
		HttpClientModule,
		// Material imports
		MatInputModule,
		MatFormFieldModule,
		MatSelectModule,
		MatRadioModule,
		MatIconModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatDialogModule,
		MatButtonModule,
		MatCheckboxModule,
		MatAutocompleteModule,
		FormsModule,
		A11yModule,
		NgOptimizedImage,
	],
    providers: [
        { provide: ErrorHandler, useFactory: errorHandlerFactory },
        ApiService,
        MatDatepickerModule,
        MatNativeDateModule,
        TrackingService,
        DatePipe,
        { provide: HTTP_INTERCEPTORS, useClass: FormSessionInterceptor, multi: true },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'outline',
                hideRequiredMarker: true
            }
        },
        { provide: APP_BASE_HREF, useValue: './' },
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
        {
            provide: APP_INITIALIZER,
            useFactory: ( affiliateConfigService: AffiliateConfigService ) => () => affiliateConfigService.load(),
            deps: [ AffiliateConfigService ],
            multi: true,
        }
    ],
    bootstrap: [AppComponent]
} )
export class AppModule {
}
